import React, { useMemo } from 'react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectGroup, SelectLabel, SelectItem } from '../ui/select';
import { Badge } from '../ui/badge';

/**
 * Selettore asset con nome esteso e gruppi per asset class.
 *
 * Props:
 *  - value: ticker selezionato (es. "SPY")
 *  - onChange: (ticker) => void
 *  - allowedSymbols: array di ticker filtrabili (es. universe della strategia attiva).
 *    Se omesso, mostra tutto.
 *  - universe: array di {symbol, name, asset_class, notes} da /universe
 *  - classLabels: { [class]: label } da /universe
 *  - placeholder: testo placeholder se value vuoto
 */
const AssetSelect = ({ value, onChange, allowedSymbols, universe, classLabels, placeholder = 'Scegli asset' }) => {
  // Filtro per simboli ammessi
  const filtered = useMemo(() => {
    if (!universe) return [];
    if (!allowedSymbols || !allowedSymbols.length) return universe;
    const allowSet = new Set(allowedSymbols);
    return universe.filter((a) => allowSet.has(a.symbol));
  }, [universe, allowedSymbols]);

  // Raggruppo per asset_class mantenendo l'ordine di CLASS_LABELS
  const groups = useMemo(() => {
    if (!filtered.length) return [];
    const byClass = {};
    for (const a of filtered) {
      const k = a.asset_class || 'other';
      if (!byClass[k]) byClass[k] = [];
      byClass[k].push(a);
    }
    // Ordine: prima quelli con label, poi alphabetico
    const order = Object.keys(classLabels || {});
    return Object.keys(byClass).sort((x, y) => {
      const ix = order.indexOf(x); const iy = order.indexOf(y);
      if (ix !== -1 && iy !== -1) return ix - iy;
      if (ix !== -1) return -1;
      if (iy !== -1) return 1;
      return x.localeCompare(y);
    }).map((k) => ({ key: k, label: (classLabels || {})[k] || k, items: byClass[k] }));
  }, [filtered, classLabels]);

  // Per il SelectValue: mostra "SPY · SPDR S&P 500" se trovato
  const selectedLabel = useMemo(() => {
    if (!value) return null;
    const a = (universe || []).find((x) => x.symbol === value);
    if (!a) return value;
    return `${a.symbol} · ${a.name}`;
  }, [value, universe]);

  return (
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className="text-sm">
        <SelectValue placeholder={placeholder}>
          {selectedLabel || placeholder}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-[60vh]">
        {groups.length === 0 ? (
          <SelectItem disabled value="_none">Nessun asset disponibile</SelectItem>
        ) : (
          groups.map((g) => (
            <SelectGroup key={g.key}>
              <SelectLabel className="flex items-center justify-between">
                <span>{g.label}</span>
                <Badge variant="outline" className="text-[9px] ml-2">{g.items.length}</Badge>
              </SelectLabel>
              {g.items.map((a) => (
                <SelectItem key={a.symbol} value={a.symbol}>
                  <span className="font-mono mr-2">{a.symbol}</span>
                  <span className="text-muted-foreground">{a.name}</span>
                </SelectItem>
              ))}
            </SelectGroup>
          ))
        )}
      </SelectContent>
    </Select>
  );
};

export default AssetSelect;
